import { HookFormSelect, HookFormTextField } from "@/shared/ui";
import { Button, Flex } from "@radix-ui/themes";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  JourneyEvent,
  addJourneyEvent,
  selectActiveJourneyEvents,
  selectAnnotationData,
  selectSelectedJourneyEvents,
  setAnnotationData,
  setCurrentCamera,
  setHighlightedTimelineEventId,
  setSelectedEventById,
} from "@/entities/annotation";
import { toast } from "react-hot-toast";
import { useTrackedHotkeys } from "@/shared/hooks/useTrackedHotkeys";
import { ANNOTATION_TYPES } from "@/entities/annotation/model/consts";
import { AnnotationFormWrapper } from "../../ui/annotation-form-wrapper";
import { useCameras } from "../../hooks/useCameras";
import { useTimelineData } from "../../hooks/useTimelineData";
import { hasIdenticalAnnotationTimestamp } from "../../lib/helpers";
import { displayUUID, formatAbsoluteCoordinates } from "@/shared";
import { InfoItem } from "@/components/info-item";
import { usePlayer } from "@tezzro/player";

const EVENT_TYPES = ["exit", "enter"] as const;
type EventType = (typeof EVENT_TYPES)[number];

const schema = z.object({
  eventType: z.enum(EVENT_TYPES),
  notes: z.string().optional(),
});

type Schema = z.infer<typeof schema>;

export function JourneyAnnotationForm() {
  const dispatch = useAppDispatch();
  const { addTimelineCustomerJourneyEvent } = useTimelineData();
  const player = usePlayer();
  const cameraData = useCameras();

  const annotationData = useAppSelector(selectAnnotationData);
  const selectedJourneyEvents = useAppSelector((state) =>
    selectSelectedJourneyEvents(state)
  );
  const activeJourneyEvents = useAppSelector(selectActiveJourneyEvents);

  const allEventOptions = EVENT_TYPES.map((type) => ({
    label: type,
    value: type,
  }));

  const availableEventOptions = allEventOptions.filter((option) =>
    selectedJourneyEvents.length === 0
      ? true
      : selectedJourneyEvents.some((event) => event.eventType !== option.value)
  );

  const pinCoordinates = formatAbsoluteCoordinates(
    annotationData?.pinCoordinates?.relativeX,
    annotationData?.pinCoordinates?.relativeY,
    annotationData?.cameraImageWidth,
    annotationData?.cameraImageHeight
  );

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    values: {
      eventType: (availableEventOptions.length === 1
        ? availableEventOptions[0].value
        : "") as EventType,
    },
  });

  const onSubmit = async (data: Schema) => {
    if (!annotationData?.pinCoordinates) {
      toast.error("Cannot get marker information. Please, try again");
      return;
    }

    if (
      hasIdenticalAnnotationTimestamp(
        activeJourneyEvents,
        annotationData?.timestamp
      )
    ) {
      toast.error(
        "Same type annotations within a journey must have different timestamps"
      );
      return;
    }

    const customId = crypto.randomUUID();

    const newJourneyEvent: JourneyEvent = {
      id: customId,
      pinCoordinates: {
        relativeX: annotationData?.pinCoordinates.relativeX,
        relativeY: annotationData?.pinCoordinates.relativeY,
      },
      type: ANNOTATION_TYPES.JOURNEY,
      eventType: data.eventType,
      journeyId: annotationData.journeyId,
      notes: data.notes,
      orderId: annotationData.orderId,
      cameraId: annotationData.cameraId,
      timestamp: new Date(annotationData.timestamp),
      cameraImageWidth: annotationData?.cameraImageWidth,
      cameraImageHeight: annotationData?.cameraImageHeight,
    };
    dispatch(addJourneyEvent(newJourneyEvent));
    dispatch(setSelectedEventById(customId));
    dispatch(setHighlightedTimelineEventId(customId));
    addTimelineCustomerJourneyEvent({
      id: newJourneyEvent.id,
      type: ANNOTATION_TYPES.JOURNEY,
      journeyId: newJourneyEvent.journeyId,
      timestamp: newJourneyEvent?.timestamp?.toISOString(),
    });
    dispatch(setAnnotationData(null));
    player.seek(annotationData?.timestamp, annotationData.cameraId);
    dispatch(setCurrentCamera(annotationData.cameraId));
    cameraData.setCameraModeFromCameraId(annotationData.cameraId);
  };

  const handleCancel = () => {
    dispatch(setAnnotationData(null));
  };

  useTrackedHotkeys("esc", (e) => {
    e.preventDefault();
    dispatch(setAnnotationData(null));
  });

  useTrackedHotkeys("mod + enter", (e) => {
    e.preventDefault();
    if (!form.formState.isSubmitting) {
      form.handleSubmit(onSubmit)();
    }
  });

  return (
    <AnnotationFormWrapper>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Flex direction="column">
          <HookFormSelect
            name="eventType"
            formMethods={form}
            label="Event type"
            options={availableEventOptions}
          />
          <InfoItem
            label="Journey ID"
            copyValue={annotationData?.journeyId}
            value={displayUUID(annotationData?.journeyId)}
          />
          <InfoItem
            label="Order ID"
            copyValue={annotationData?.orderId}
            value={displayUUID(annotationData?.orderId)}
          />
          <InfoItem label="Timestamp" value={annotationData?.timestamp} />
          <InfoItem label="Pin coordinates (x,y)" value={pinCoordinates} />
          <InfoItem
            label="Camera image dimensions"
            value={`${annotationData?.cameraImageWidth}x${annotationData?.cameraImageHeight}`}
          />
          <InfoItem label="Annotated camera" value={annotationData?.cameraId} />
          <HookFormTextField name="notes" formMethods={form} label="Note" />
        </Flex>
        <Flex justify="between" mt="8">
          <Button
            size="1"
            color="gray"
            variant="soft"
            type="button"
            onClick={handleCancel}
          >
            Cancel (esc)
          </Button>
          <Button
            size="1"
            color="gray"
            highContrast
            type="submit"
            loading={form.formState.isSubmitting}
          >
            Apply (ctrl + enter)
          </Button>
        </Flex>
      </form>
    </AnnotationFormWrapper>
  );
}
